
import React, { useMemo, useState, useEffect } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl, FormControlLabel,
    FormLabel,
    Grid,
    InputLabel,
    MenuItem,
    OutlinedInput, Radio, RadioGroup,
    Select,
    Switch,
    TextField,
    Tooltip, Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import moment from "moment";
import { MobileDatePicker } from "@mui/x-date-pickers";
import {api} from "../../../services/Main/Api";
import dayjs from "dayjs";
import DurationSelector from "../../../components/DurationSelector/DurationSelector";

// --- componente utilitário para dinheiro (bem simples; troque pelo seu Masks.money se quiser)
const formatMoneyBRL = (raw) => {
    if (!raw) return "R$ 0,00";
    const digits = String(raw).replace(/\D/g, "");
    const int = (parseInt(digits, 10) || 0).toString();
    const withCents = int.padStart(3, "0");
    const reais = withCents.slice(0, -2);
    const cents = withCents.slice(-2);
    return `R$ ${reais.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${cents}`;
};
const parseMoneyToIntCents = (label) => {
    if (!label) return 0;
    const digits = String(label).replace(/\D/g, "");
    return parseInt(digits, 10) || 0;
};

// --- Select de dias da semana com ISO (Seg=1..Dom=7)
const DAYS_OF_WEEK = [
    { value: 0, label: "Segunda-feira" },
    { value: 1, label: "Terça-feira" },
    { value: 2, label: "Quarta-feira" },
    { value: 3, label: "Quinta-feira" },
    { value: 4, label: "Sexta-feira" },
    { value: 5, label: "Sábado" },
    { value: 6, label: "Domingo" },
];

const WeekdayMultiSelect = ({ value, onChange }) => {
    const MenuProps = useMemo(
        () => ({
            PaperProps: { style: { maxHeight: 48 * 4.5 + 8, width: 280 } },
        }),
        []
    );

    return (
        <FormControl fullWidth>
            <InputLabel id="per-day-label">Dias da semana</InputLabel>
            <Select
                labelId="per-day-label"
                id="per-day-select"
                multiple
                value={value}
                onChange={(e) => {
                    const v = e.target.value;
                    onChange(typeof v === "string" ? v.split(",").map(Number) : v);
                }}
                input={<OutlinedInput label="Dias da semana" />}
                renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((dayValue) => {
                            const dayObj = DAYS_OF_WEEK.find((d) => d.value === dayValue);
                            return <Chip key={dayValue} label={dayObj?.label || dayValue} />;
                        })}
                    </Box>
                )}
                MenuProps={MenuProps}
            >
                {DAYS_OF_WEEK.map((day) => (
                    <MenuItem key={day.value} value={day.value}>
                        {day.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

// --- tipos de recorrência (pode vir do backend; deixei defaults)
const DEFAULT_RECURRING_TYPES = [
    { id: "DAILY", value: "Diariamente" },
];

export default function RentDailyFeeForm({
                                             open,
                                             onClose,
                                             onSaved,
                                             rentId,

                                         }) {
    // Campos principais
    const [category, setCategory] = useState(null);
    const [val, setVal] = React.useState({ startDate: dayjs(), endDate: null });
    const [isSaving, setIsSaving] = useState(false);
    const [categoryTag, setCategoryTag] = useState(null);
    const [categoryChoices, setCategoryChoices] = useState([]);
    const [valueLabel, setValueLabel] = useState("R$ 0,00");
    const [startDate, setStartDate] = useState(moment().toDate());
    const [endDate, setEndDate] = useState(() => moment().add(1, "month").toDate());
    const [description, setDescription] = useState("");
    const [invoiceRule, setInvoiceRule] = useState("INVOICE_SUM_VALUE");
    const [transferRule, setTransferRule] = useState("TRANSFER_SUM_VALUE");
    const [invoiceRuleChoices, setInvoiceRuleChoices] = useState([]);
    const [transferRuleChoices, setTransferRuleChoices] = useState([]);

    // Recorrência
    const [recurring, setRecurring] = useState(true);
    const [recurringType, setRecurringType] = useState("DAILY");
    const [recurringCustom, setRecurringCustom] = useState(6); // meses
    const [perDaySelected, setPerDaySelected] = useState([0, 1, 2, 3, 4, 5, 6]); // [1..7]
    const [recurringText, setRecurringText] = useState("Sem data de término");

    // Erros simples de validação
    const [errors, setErrors] = useState(null);

    // Atualiza texto da recorrência
    useEffect(() => {
        if (!recurring) {
            setRecurringText("Sem recorrência");
            return;
        }
        if (recurringType === "EVERYDAY") {
            setRecurringText("Sem data de término (todo dia)");
            return;
        }
        if (recurringType === "DAILY") {
            const dias = perDaySelected
                .map((d) => DAYS_OF_WEEK.find((x) => x.value === d)?.label || d)
                .join(", ");
            setRecurringText(dias ? `Dias: ${dias}` : "Selecione os dias da semana");
            return;
        }
        const mapMeses = {
            THREE_MONTHS: 3,
            SIX_MONTHS: 6,
            TWELVE_MONTHS: 12,
            CUSTOM: recurringCustom,
        };
        const meses = mapMeses[recurringType];
        if (!meses) {
            setRecurringText("Selecione um período");
            return;
        }
        const end = moment(startDate).add(meses, "months");
        setRecurringText(`Recorrente até ${end.format("MMMM [de] YYYY")}`);
    }, [recurring, recurringType, recurringCustom, startDate, perDaySelected]);

    const handleClear = () => {
        setCategory(null);
        setValueLabel("R$ 0,00");
        setStartDate(moment().toDate());
        setEndDate(moment().add(1, "day").toDate());
        setDescription("");
        setRecurring(false);
        setRecurringType("EVERYDAY");
        setRecurringCustom(6);
        setPerDaySelected([]);
        setErrors(null);
    };

    const validate = () => {
        const e = {};
        if (!category) e.category = "Informe a categoria.";
        if (parseMoneyToIntCents(valueLabel) <= 0) e.value = "Informe um valor maior que zero.";
        if (!startDate) e.startDate = "Informe a data de início.";
        //if (!endDate) e.endDate = "Informe a data final.";
        if (startDate && endDate && moment(endDate).isBefore(moment(startDate), "day")) {
            e.range = "A data final não pode ser anterior à data de início.";
        }
        if (recurring && recurringType === "DAILY" && perDaySelected.length === 0) {
            e.perDay = "Selecione pelo menos um dia da semana.";
        }
        setErrors(Object.keys(e).length ? e : null);
        return Object.keys(e).length === 0;
    };

    function resetFields() {

        setValueLabel("0,00")
        setRecurring(false)
        setInvoiceRule("INVOICE_SUM_VALUE")
        setTransferRule("NO_ACTION")

        setCategory("")
        setCategoryTag("")

        setDescription("")

        setErrors(null)
        setPerDaySelected([0,1,2,3,4,5,6])

        setStartDate(moment().toDate())
        setEndDate(moment().add(1, "month").toDate())
    }

    const handleSubmit = () => {
        if (!validate()) return;

        setIsSaving(true)

        const value = parseInt((valueLabel || '').replace(/[^0-9]/g, '')) || 0
        const payload = {
            value,
            year: startDate.getFullYear(),
            month: startDate.getMonth(),
            category: categoryTag,
            category_fee: category, // ← envia TAG no lugar de ID
            recurring,
            description,
            payment: false,
            installment_start: null,
            installments: false,
            invoice_rule: invoiceRule,
            transfer_rule: transferRule,
            rent_contract: rentId,
            recurring_type: null,
            recurring_end_month: null,
            recurring_end_year: null,
            reference_date: moment(startDate).format("YYYY-MM-DD"),
            reference_end_date: moment(endDate).format("YYYY-MM-DD"),
            days_of_week: perDaySelected.toString()
        };


        api.post("/rent-contract/fee/", payload)
            .then(() => {
                // feedback no pai
                onSaved?.()
                resetFields()
                onClose?.()
            })
            .catch((error) => {
                console.log(error);
                setErrors(error?.response?.data);
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    const handleChoices = () => {
        let params = { rent_contract: rentId }
        api.get("/rent-contract/choices/", {params}).then(response => {
            setCategoryChoices(response.data.category_choices || [])
            setInvoiceRuleChoices(response.data.invoice_choices || [])
            setTransferRuleChoices(response.data.transfer_choices || [])
        }).catch(console.log)
    }

    useEffect(() => {
        handleChoices()
    }, [])

    return (
        <Dialog fullWidth maxWidth="md" open={open} onClose={onClose} scroll="body">
            <DialogTitle>Nova cobrança</DialogTitle>
            <Divider />

            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        {errors && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {Object.values(errors).map((msg, idx) => (
                                    <div key={idx}>{msg}</div>
                                ))}
                            </Alert>
                        )}
                    </Grid>

                    {/* Categoria */}


                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth required>
                            <InputLabel id="category-label">Categoria</InputLabel>
                            <Select
                                labelId="category-label"
                                label="Categoria"
                                value={category}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    const found = categoryChoices.find(c => c.id === value); // pega objeto
                                    setCategoryTag(found?.tag || '');
                                    setCategory(value)

                                }}
                                input={<OutlinedInput label="Categoria" />}
                            >
                                {categoryChoices.map((opt) => (
                                    <MenuItem key={opt.id ?? opt.value} value={opt.id ?? opt.value}>
                                        {opt.value ?? opt.label ?? String(opt)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth required>
                            <InputLabel>Valor diário</InputLabel>
                            <OutlinedInput
                                label="Valor diário"
                                value={valueLabel}
                                onChange={(e) => setValueLabel(formatMoneyBRL(e.target.value))}
                            />
                        </FormControl>
                    </Grid>

                    {/* Datas */}

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>

                            <MobileDatePicker
                                labelId="value-type-select-label"
                                label="Data de inicio"
                                value={startDate}
                                onChange={(v) => setStartDate(v)}
                                slotProps={{ textField: { size: "large" } }}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>


                            <MobileDatePicker
                                label="Data final"
                                value={endDate}
                                onChange={(v) => setEndDate(v)}
                                slotProps={{ textField: { size: "large" } }}
                            />
                        </FormControl>
                    </Grid>



                    {/* Descrição */}
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <TextField
                                label="Descrição"
                                placeholder="Descrição"
                                multiline
                                rows={2}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <Divider />
            <DialogTitle>Regras</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} mt={.5}>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel id="invoice-select-label">Fatura</InputLabel>
                            <Select
                                labelId="invoice-select"
                                id="invoice-select"
                                value={invoiceRule}
                                label="Fatura"
                                disabled={category === "ADMINISTRATIVE_FEE"}
                                onChange={(e)=> setInvoiceRule(e.target.value)}
                            >
                                {invoiceRuleChoices.map((item, index) => (
                                    <MenuItem key={index} value={item.id}>{item.value}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel id="transfer-select-label">Repasse</InputLabel>
                            <Select
                                labelId="transfer-select"
                                id="transfer-select"
                                value={transferRule}
                                label="Repasse"
                                onChange={(e)=> setTransferRule(e.target.value)}
                            >
                                {transferRuleChoices.map((item, index) => (
                                    <MenuItem key={index} value={item.id}>{item.value}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>
            <Divider />
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
                Recorrência

            </DialogTitle>

            {recurring && (
                <DialogContent>
                    <Grid container spacing={2} sx={{mt:1}}>

                        <Grid item xs={12} md={12}>
                            {recurringType === "DAILY" &&
                                <>
                                <WeekdayMultiSelect
                                    value={perDaySelected}
                                    onChange={setPerDaySelected}
                                />
                                    <Box sx={{mt:2}}>
                                        <Button size={"small"} onClick={()=>setPerDaySelected([0,1,2,3,4])} variant={"outlined"}>SEG-SEX</Button>
                                        <Button size={"small"} onClick={()=>setPerDaySelected([5,6])} variant={"outlined"} sx={{mx: 1}}>SAB-DOM</Button>
                                        <Button size={"small"} onClick={()=>setPerDaySelected([0,1,2,3,4,5,6])} variant={"outlined"} >TODOS</Button>
                                    </Box>
                                </>
                           }
                        </Grid>

                        {/* hint */}
                        <Grid item xs={12}>
                            <Tooltip title="Os dias da semana">
                                <Box sx={{ color: "text.secondary", display: "flex", alignItems: "center", gap: 1 }}>
                                    <InfoOutlinedIcon fontSize="small" />
                                    {recurringText}
                                </Box>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </DialogContent>
            )}



            <Divider />
            <DialogActions sx={{ display: "flex", justifyContent: "space-between", p: "1rem" }}>
                <Box>
                    <Button variant="text" onClick={onClose}>Cancelar</Button>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>

                    <Button variant="contained" disabled={isSaving} onClick={handleSubmit}>{isSaving ? "Salvando cobrança ...": "Salvar" }</Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}
